import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { IResponse } from '../models/chat.models';
import { ProjectInstructions } from '../models/project-instructions.model';
import { ProjectInstructionKind, ProjectInstructionSectionRecord } from '../models/project-instruction-section.model';
import { AIAgentService } from './ai-agent.service';
import { DeoOwnershipService } from './deo-ownership.service';

const INSTRUCTIONS_KIND: ProjectInstructionKind = 'instructions';

/**
 * Owns a project's instructions: what is saved, and what the user has typed but not saved.
 *
 * Saved instructions are the backend's (DATA-1566). Only the unsaved draft is held here, in memory:
 * it is domain data on its way to the endpoint, so it does not go in `UiStateStorageService`.
 */
@Injectable({
  providedIn: 'root'
})
export class ProjectInstructionsService {
  /**
   * Typed-but-unsaved text, keyed by project id.
   *
   * The panel is destroyed whenever the user switches to Chats or Artifacts, so a draft held on the
   * component would be lost on a click that never claimed to discard anything.
   */
  private readonly _draftByProjectId = new Map<string, string>();

  /** The version each project last loaded or saved, sent back as `base_version` so a stale save is a 409. */
  private readonly _versionByProjectId = new Map<string, number>();

  /**
   * The version a draft was started from. A reload moves `_versionByProjectId` on but keeps the draft,
   * so saving that draft against the newer version would overwrite a save made elsewhere without a 409.
   */
  private readonly _draftBaseVersionByProjectId = new Map<string, number>();

  constructor(
    private readonly _aiAgentService: AIAgentService,
    private readonly _ownershipService: DeoOwnershipService
  ) {}

  /** The instructions a project has saved, and every revision behind them. */
  loadInstructions(projectId: string): Observable<ProjectInstructions> {
    return this._aiAgentService.getProjectInstructions(projectId, INSTRUCTIONS_KIND).pipe(map(response => this.toInstructions(projectId, response)));
  }

  /**
   * Saves the text as a new revision and returns the instructions as they now stand.
   *
   * The draft is settled here, not by the panel, so it still happens when the panel has moved to
   * another project before the answer arrives. A 409 drops it too: the newer version wins.
   */
  saveInstructions(projectId: string, text: string): Observable<ProjectInstructions> {
    const baseVersion = this._draftBaseVersionByProjectId.get(projectId) ?? this._versionByProjectId.get(projectId) ?? 0;
    const request = { content: text, base_version: baseVersion };
    return this._aiAgentService.saveProjectInstructions(projectId, INSTRUCTIONS_KIND, request).pipe(
      map(response => this.toInstructions(projectId, response)),
      tap({
        next: () => this.clearDraft(projectId),
        error: error => {
          if (error instanceof HttpErrorResponse && error.status === 409) {
            this.clearDraft(projectId);
          }
        }
      })
    );
  }

  /**
   * Text the user typed but has not saved, or `null` when there is none.
   *
   * Tests `has` rather than truthiness: an emptied box is a real draft ("I cleared it"), and reading
   * it as "no draft" would put the saved text back the next time the panel mounts.
   */
  draftFor(projectId: string): string | null {
    return this._draftByProjectId.has(projectId) ? (this._draftByProjectId.get(projectId) as string) : null;
  }

  /** Remembers unsaved text so it survives a trip to another tab. */
  setDraft(projectId: string, text: string): void {
    if (!projectId) {
      return;
    }
    this._draftByProjectId.set(projectId, text);
    if (!this._draftBaseVersionByProjectId.has(projectId) && this._versionByProjectId.has(projectId)) {
      this._draftBaseVersionByProjectId.set(projectId, this._versionByProjectId.get(projectId) as number);
    }
  }

  /** Forgets unsaved text, after a save or a discard. */
  clearDraft(projectId: string): void {
    this._draftByProjectId.delete(projectId);
    this._draftBaseVersionByProjectId.delete(projectId);
  }

  /** A 200 with `succeeded: false` is rethrown so callers have one error path, still read by `getApiErrorMessage`. */
  private toInstructions(projectId: string, response: IResponse<ProjectInstructionSectionRecord>): ProjectInstructions {
    if (!response?.succeeded || !response.data) {
      throw new HttpErrorResponse({ error: { ...response, succeeded: false }, status: 200 });
    }

    const record = response.data;
    this._versionByProjectId.set(projectId, record.version);
    return this.mapRecord(projectId, record);
  }

  private mapRecord(projectId: string, record: ProjectInstructionSectionRecord): ProjectInstructions {
    const isSaved = record.version > 0;
    return {
      projectId,
      text: record.content ?? '',
      savedAt: isSaved ? record.created_at : null,
      savedBy: isSaved && record.created_by ? this.displaySavedBy(record.created_by) : null,
      version: record.version,
      canEdit: record.can_edit,
      characterLimit: record.character_limit,
      versions: (record.versions ?? []).map(version => ({
        tag: version.tag,
        text: version.content,
        savedAt: version.created_at,
        savedBy: this.displaySavedBy(version.created_by)
      }))
    };
  }

  /** The API names the saver by email; there is no lookup to turn that into a name. */
  private displaySavedBy(email: string): string {
    const currentEmail = this._ownershipService.currentUserEmail().trim().toLowerCase();
    return currentEmail && email?.trim().toLowerCase() === currentEmail ? 'You' : email;
  }
}
